/**
 * JSON-LD structured-data builders.
 *
 * Structured data is the single highest-leverage signal for AEO: it tells AI
 * answer engines and search platforms *what* an entity is (an Organization, a
 * Service, an FAQ, an article) in a machine-readable way, rather than making
 * them infer it from rendered text. Each builder returns a plain object that
 * `<Seo jsonLd={...}>` serialises into a <script type="application/ld+json">.
 */
import {
  SITE_NAME,
  SITE_URL,
  DEFAULT_DESCRIPTION,
  DEFAULT_OG_IMAGE,
  DEFAULT_LOGO,
  absoluteUrl,
  SOCIAL_URLS,
  CONTACT_EMAIL,
  FOUNDERS,
  ORG_ID,
  WEBSITE_ID,
} from "./config";

type Json = Record<string, unknown>;

/**
 * Verified brand entity URLs — these link the Organization to its social
 * profiles / third-party listings so search + AI engines can resolve "Cello"
 * unambiguously. Sourced from `config.ts` (also mirrored in index.html's static
 * Organization block). Add a URL only once it's confirmed — a wrong one is worse
 * than none.
 */
export const ORGANIZATION_SAME_AS: string[] = [SOCIAL_URLS.instagram, SOCIAL_URLS.linkedin];

/** The founders as Person nodes (Organization.founder + standalone Person schema). */
const founderPersons = (): Json[] =>
  FOUNDERS.map((name) => ({ "@type": "Person", name }));

/** The Cello brand as an Organization. Emitted site-wide (static in index.html +
 *  on the home page). The shared `@id` merges the two into one entity. */
export const organizationLd = (): Json => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": ORG_ID,
  name: SITE_NAME,
  url: SITE_URL,
  logo: DEFAULT_LOGO,
  image: DEFAULT_OG_IMAGE,
  description: DEFAULT_DESCRIPTION,
  founder: founderPersons(),
  contactPoint: {
    "@type": "ContactPoint",
    email: CONTACT_EMAIL,
    contactType: "customer support",
  },
  ...(ORGANIZATION_SAME_AS.length > 0 && { sameAs: ORGANIZATION_SAME_AS }),
});

/** The site itself — helps engines resolve the canonical name for the domain. */
export const websiteLd = (): Json => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": WEBSITE_ID,
  name: SITE_NAME,
  url: SITE_URL,
  publisher: { "@id": ORG_ID },
});

/**
 * The core offering: personal styling. `serviceType`/`areaServed` can be
 * overridden per page (e.g. a city page passes areaServed: "San Francisco").
 */
export const serviceLd = (opts?: { name?: string; areaServed?: string | Json; description?: string }): Json => ({
  "@context": "https://schema.org",
  "@type": "Service",
  serviceType: "Personal styling",
  name: opts?.name ?? "Cello personal styling",
  description: opts?.description ?? DEFAULT_DESCRIPTION,
  provider: { "@id": ORG_ID },
  areaServed: opts?.areaServed ?? "US",
});

/**
 * A structured City for a service's `areaServed` — richer local-intent signal
 * than a bare string, without falsely claiming a physical LocalBusiness storefront
 * (Cello is a remote service that *serves* these cities, it isn't located in them).
 */
export const cityAreaServed = (city: string, state: string): Json => ({
  "@type": "City",
  name: city,
  containedInPlace: { "@type": "State", name: state },
});

export interface FaqItem {
  question: string;
  answer: string;
}

/** FAQPage — makes the /faq answers eligible as direct answers in AI/search results. */
export const faqPageLd = (items: FaqItem[]): Json => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: items.map((it) => ({
    "@type": "Question",
    name: it.question,
    acceptedAnswer: {
      "@type": "Answer",
      text: it.answer,
    },
  })),
});

export interface BlogPostingInput {
  title: string;
  description?: string | null;
  slug: string;
  image?: string | null;
  datePublished?: string | null;
  dateModified?: string | null;
  author?: string | null;
}

/** BlogPosting — the article schema AI engines cite when surfacing a post. */
export const blogPostingLd = (post: BlogPostingInput): Json => ({
  "@context": "https://schema.org",
  "@type": "BlogPosting",
  headline: post.title,
  description: post.description ?? undefined,
  image: post.image ? absoluteUrl(post.image) : DEFAULT_OG_IMAGE,
  datePublished: post.datePublished ?? undefined,
  dateModified: post.dateModified ?? post.datePublished ?? undefined,
  author: {
    "@type": post.author ? "Person" : "Organization",
    name: post.author ?? SITE_NAME,
  },
  publisher: {
    "@type": "Organization",
    name: SITE_NAME,
    logo: { "@type": "ImageObject", url: DEFAULT_LOGO },
  },
  mainEntityOfPage: {
    "@type": "WebPage",
    "@id": absoluteUrl(`/blog/${post.slug}`),
  },
  url: absoluteUrl(`/blog/${post.slug}`),
});

export interface ReviewInput {
  name: string;
  quote: string;
  rating: number;
}

/**
 * Individual Review, nested inside the Organization/Service so search engines
 * can attribute the review to the brand entity, not a stray page. Uses only
 * the fields we actually have (no reviewer profile URL, no date) — schema.org
 * accepts a minimal Review as long as `author`, `reviewRating`, and
 * `reviewBody` are present.
 */
export const reviewLd = (review: ReviewInput): Json => ({
  "@type": "Review",
  author: { "@type": "Person", name: review.name },
  reviewRating: {
    "@type": "Rating",
    ratingValue: review.rating,
    bestRating: 5,
  },
  reviewBody: review.quote,
});

/**
 * AggregateRating summarises the Review list so Google's rich-results eligibility
 * treats testimonials as first-party reviews of the Organization (not third-party).
 * Attach as a property of the Organization/Service, not standalone.
 */
export const aggregateRatingLd = (opts: { ratingValue: number; reviewCount: number }): Json => ({
  "@type": "AggregateRating",
  ratingValue: opts.ratingValue,
  reviewCount: opts.reviewCount,
  bestRating: 5,
  worstRating: 1,
});

/**
 * Organization enriched with reviews + aggregate rating. Use on the home
 * route in place of `organizationLd()` when we want testimonials attached to
 * the brand entity (schema.org requires reviews to be children of a real
 * entity, not floating at the page level).
 */
export const organizationWithReviewsLd = (reviews: ReviewInput[]): Json => {
  const base = organizationLd() as Record<string, unknown>;
  if (reviews.length === 0) return base;
  const sum = reviews.reduce((a, r) => a + r.rating, 0);
  const avg = Math.round((sum / reviews.length) * 10) / 10;
  return {
    ...base,
    aggregateRating: aggregateRatingLd({ ratingValue: avg, reviewCount: reviews.length }),
    review: reviews.map(reviewLd),
  };
};

/** Breadcrumb trail — helps engines understand site hierarchy. */
export const breadcrumbLd = (items: { name: string; path: string }[]): Json => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    item: absoluteUrl(it.path),
  })),
});

export interface HowToStep {
  name: string;
  text: string;
}

/**
 * HowTo — makes a step-by-step process (e.g. "How Cello works") eligible for
 * how-to rich results and directly extractable by AI answer engines. Steps are
 * the same ones shown on the page, so the schema mirrors the visible content.
 */
export const howToLd = (opts: { name: string; description?: string; steps: HowToStep[] }): Json => ({
  "@context": "https://schema.org",
  "@type": "HowTo",
  name: opts.name,
  description: opts.description ?? DEFAULT_DESCRIPTION,
  step: opts.steps.map((s, i) => ({
    "@type": "HowToStep",
    position: i + 1,
    name: s.name,
    text: s.text,
  })),
});

/**
 * A named Person (founder / stylist) — strengthens the entity graph and E-E-A-T.
 * `worksFor` links the person to the Organization by its shared `@id`.
 */
export const personLd = (opts: {
  name: string;
  jobTitle?: string;
  sameAs?: string[];
}): Json => ({
  "@context": "https://schema.org",
  "@type": "Person",
  name: opts.name,
  ...(opts.jobTitle && { jobTitle: opts.jobTitle }),
  worksFor: { "@id": ORG_ID },
  ...(opts.sameAs && opts.sameAs.length > 0 && { sameAs: opts.sameAs }),
});
